'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { webPagesClient } from '@terra/shared-rest-clients-web-pages';
import { toasterApi } from '../../toast/ToastProvider';
import { WEBPAGE_FEATURES_QUERY_KEY } from './applicationAssetsQueryKeys';

type RunFeatureOverrideParams = {
  webpageId: string;
  featureId: string;
  providedExecutionContext?: string;
  forceRerun?: boolean;
};

type RunFeatureOverrideOptions = {
  onSuccess?: () => void;
};

const getErrorMessage = ({
  body,
  fallback,
}: {
  body: unknown;
  fallback: string;
}): string => {
  if (typeof body === 'object' && body !== null) {
    const { message } = body as { message?: unknown };

    if (typeof message === 'string') {
      return message;
    }
  }

  return fallback;
};

export const useFeatureOverrideRun = () => {
  const queryClient = useQueryClient();
  const t = useTranslations(
    'designSystem.applicationAssets.drawer.features.overrideRun.toast',
  );

  const mutation = useMutation({
    mutationFn: async ({
      webpageId,
      featureId,
      providedExecutionContext,
      forceRerun,
    }: RunFeatureOverrideParams) => {
      const trimmedContext = providedExecutionContext?.trim();
      const response = await webPagesClient.executeFeatureOverride({
        params: { id: webpageId, featureId },
        body: {
          overridePolicy: true,
          ...(trimmedContext
            ? { providedExecutionContext: trimmedContext }
            : {}),
          ...(forceRerun ? { forceRerun } : {}),
        },
      });

      if (response.status < 200 || response.status >= 300) {
        throw new Error(
          getErrorMessage({
            body: response.body,
            fallback: t('startFailedDescription'),
          }),
        );
      }

      return response.body;
    },
    onSuccess: (_response, { webpageId }) => {
      void queryClient.invalidateQueries({
        queryKey: [WEBPAGE_FEATURES_QUERY_KEY, webpageId],
      });
      toasterApi.success({
        title: t('startedTitle'),
        description: t('startedDescription'),
      });
    },
    onError: (error: Error) => {
      toasterApi.error({
        title: t('startFailedTitle'),
        description: error.message || t('startFailedDescription'),
      });
    },
  });

  return {
    runFeatureOverride: (
      params: RunFeatureOverrideParams,
      options?: RunFeatureOverrideOptions,
    ) => mutation.mutate(params, { onSuccess: options?.onSuccess }),
    isRunningFeatureOverride: mutation.isPending,
  };
};
